"use client";;
import useDebounce from '@/app/hooks/useDebounce';
import { Button } from '@/app/ui/forms/button';
import { FormItemCustom } from '@/app/ui/forms/FormItems';
import { Input } from '@/app/ui/imported/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/app/ui/imported/popover';
import { Separator } from '@/app/ui/imported/separator';
import { Skeleton } from '@/app/ui/imported/skeleton';
import SortBy, { SortByOption } from '@/app/ui/imported/SortBy';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/app/ui/imported/tooltip';
import BlurFade from '@/app/ui/magicui/blur-fade';
import { SearchVideos_action } from '@/lib/actions/public.actions';
import { ActionResponseType } from '@/lib/definitions';
import { cn, GoogleAnalyticsEvent } from '@/lib/utils';
import { videoSearchScheme } from '@/lib/zodSchemas';
import { Season, Video, Youtuber } from '@prisma/client';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { BsInfoCircleFill } from "react-icons/bs";
import { CgClose } from "react-icons/cg";
import { FaSearch } from "react-icons/fa";
import { toast } from 'sonner';
import useSWR, { SWRConfiguration_Custom } from 'swr';
import { ZodError } from 'zod';
import VideoItem from '../videos/VideoItem/VideoItem';

function VideoSearch({ yt, season }: { yt?: Youtuber, season?: Season }) {
    const [isOpen, setIsOpen] = useState(false); //main visibility controller
    const [searchTerm, setSearchTerm] = useState<string | null>(null) //current search for he server (debounced value)

    const [sortBy, setSortBy] = useState<string>("none")
    const [videos, setVideos] = useState<Video[]>([]); //resulted video array

    const [page, setPage] = useState(0); //pagination page indicator (gets rest when search is)
    const [isLastPage, setIsLastPage] = useState(false); //indicator if we have more pages (updated from server response)
    const { isLoading, data: searchRes } = useSWR<ActionResponseType<typeof SearchVideos_action>>
        (
            !searchTerm ? null : ("videoSearch_" + searchTerm + "_page_" + page + "_se_" + season?.shortName), {
                //trigger search
                "fetcher": () => SearchVideos_action({
                    search: searchTerm as string,
                    "ytid": yt?.ytid,
                    "seShortName": season?.shortName,
                    page
                }),
                "dedupingInterval": 1000 * 60 * 60, //keep in client cache for 1h (resets on client refresh)
                /** dent revalidate */
                "revalidateIfStale": false,
                "revalidateOnReconnect": false
            } as SWRConfiguration_Custom
        );

    const debounceSearch = useDebounce({
        delay: 500,
        callback: (term: string) => {
            GoogleAnalyticsEvent({ event_name: "search", search_term: term }); //track data
            setSearchTerm(term); //set real search - casing swr to send a new request
        }
    });

    function handleSearchChange(term: string) {
        setPage(0);//reset pagination state
        debounceSearch(term);
    }

    //handle response from server / swr cache
    useEffect(() => {
        //if no search yet
        if (!searchRes) return;

        //check if error
        if ("error" in searchRes) {
            toast.error(searchRes.error)
            return;
        }

        //of not videos found
        if (!searchRes.videos?.length) {
            toast.info("לא נמצאו תואמות תואמות לחיפוש.");
            setVideos([]);
            return;
        }

        setIsLastPage(searchRes.isLastPage); //shows/hide "more" options when needed
        //set or append new videos to videos state
        setVideos((prev) => (page === 0 ? searchRes.videos : [...prev, ...searchRes.videos]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchRes])

    function toggleSearch() {
        setIsOpen(pre => !pre) //toggle open state
        //on change to close
        if (!isOpen) {
            setVideos([]) //reset videos array state
            setPage(0); //reset pagination state
            setSearchTerm(null); //reset real search (swr's)
            setIsLastPage(false)
        }
    }


    return (
        <section className={ cn("max-w-screen-2xl w-full m-auto flex flex-col border-nfBlue-500 relative z-10 p-2 mb-0 mt-4 rounded-md delay-500 transition-all",
            isOpen && "bg-black/30 delay-300 border mb-4",
            isOpen && yt && "sm:mt-8"
        ) }>
            <div className="flex gap-2 w-full">
                <SearchToggleButton isOpen={ isOpen } onClick={ toggleSearch } />
                <SearchForm { ...{ isOpen, yt, season, onSearchChange: handleSearchChange, setSortBy } } />
            </div>
            <div className={ cn("max-h-svh bg-nfBlue-800/50 transition-all delay-300 duration-1000 rounded-lg overflow-hidden ",
                page >= 1 && "overflow-auto",
                (!isOpen || !isLoading && videos.length == 0) && "delay-0 max-h-0") }>
                <div className='grid grid-cols-2 lg:grid-cols-4 gap-4 p-2'>
                    { (!isLoading || page >= 1) &&
                        videos
                            .map(a => a).sort((a, b) => SortByFn(a, b, sortBy as (keyof Video | "none")))
                            .map((video, index) => (
                                <BlurFade key={ video.id } delay={ index * 0.1 - page * 4 * 0.1 }>
                                    <VideoItem video={ video } />
                                </BlurFade>
                            ))
                    }
                    { isLoading &&
                        [0, 1, 2, 3].map((_, index) => (
                            <Skeleton key={ index } className="aspect-video rounded" style={ { animationDelay: `${index * 0.3}s` } } />
                        ))
                    }
                </div>
                {
                    !isLastPage && !isLoading &&
                    <Button onClick={ () => setPage(pre => pre + 1) } className="m-auto">עוד</Button>
                }
            </div>
        </section >
    )
}
export default VideoSearch

function SearchToggleButton({ isOpen, onClick }: { isOpen: boolean, onClick: () => void }) {
    return <Tooltip>
        <TooltipTrigger asChild>
            <Button size={ "icon" } variant={ !isOpen && "outline" || "destructive" } colorScheme={ !isOpen && "green" || "none" } className="border-2 aspect-square mt-1"
                onClick={ onClick } aria-label={ isOpen && "סגירת חיפוש" || "חיפוש סרטונים" }
            >
                { !isOpen &&
                    <FaSearch /> ||
                    <CgClose />
                }
            </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
            חיפוש סרטונים
        </TooltipContent>
    </Tooltip>
}

function SearchForm({ isOpen, yt, season, onSearchChange, setSortBy }:
    { isOpen: boolean, yt?: Youtuber, season?: Season, onSearchChange: (term: string) => void, setSortBy: (sort: string) => void }) {
    const formRef = useRef<HTMLFormElement>(null);
    const [zodError, setZodError] = useState<undefined | ZodError>() //form errors

    //on form open(right before animation) -> reset states
    useEffect(() => {
        if (isOpen) {
            formRef.current?.reset();
            setZodError(undefined);
        }
    }, [isOpen])


    /**update search state & validate input + show errors */
    function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
        /**check user input */
        const validateInput = (input: string): boolean => {
            const result = videoSearchScheme.safeParse({ search: input });
            setZodError(result.error || undefined);
            return !!result.error;
        };
        const input = e.target.value;
        if (!validateInput(input)) { //
            onSearchChange(input);
        }
    }

    return <form ref={ formRef }
        onSubmit={ e => e.preventDefault() }
        className={ cn("w-0 flex gap-1 sm:gap-3 overflow-hidden py-1 transition-all duration-700 ease-in", isOpen && "w-full ease-out") }>
        <FormItemCustom id="search" item={ { search: "" } } zodError={ zodError } className='w-full ms-1'>
            <Input
                name='searchTerm'
                disabled={ !isOpen }
                onChange={ handleInputChange }
                className="border-2 focus-visible:border-nfPurple-400 flex"
                placeholder={ `חיפוש סרטונים${yt && (" של " + yt.name) || ""} מ${season?.name || "כל העונות"}` } />
        </FormItemCustom>
        <HelpInfo yt={ yt } />
        <SortBy
            name="videoSearchOptions"
            options={ sortOptions }
            onChange={ setSortBy }
            disabled={ !isOpen }
            className='me-1'
        />
    </form>
}

const sortOptions: (SortByOption & { value: keyof Video | "none" })[] = [
    {
        "label": "תאימות לחיפוש",
        "value": "none"
    },
    {
        "label": "פופולריות",
        "value": "views"
    },
    {
        "label": "תאריך פרסום",
        "value": "createdAt"
    }
]

function SortByFn(a: Video, b: Video, sortBy: keyof Video | "none") {
    if (sortBy == "none" || !sortBy)
        return 1;
    return a[sortBy] < b[sortBy] ? 1 : -1;
}

function HelpInfo({ yt }: { yt?: Youtuber }) {
    return <Popover>
        <PopoverTrigger asChild>
            <Button size={ "icon" } variant={ "outline" } colorScheme={ "blue" } className="aspect-square hidden sm:flex"><BsInfoCircleFill /></Button>
        </PopoverTrigger>
        <PopoverContent className='text-center text-sm text-shadow-sm p-2 flex flex-col gap-2 bg-nfBlue-900/50'>
            <h5>אופציות חיפוש:</h5>
            <Separator />
            <p className='bg-black/50 rounded border border-nfPurple-500'>חיפוש מתבצע לפי התאמה הכי קרובה למילים, לחיפוש מתקדם:</p>
            <p><span className='bg-black/50 rounded px-1 me-2'>&quot;ביטוי&quot;</span>
                חיפוש מדוייק של ביטוי.</p>
            { !yt &&
                <p><span className='bg-black/50 rounded px-1 me-2'>יוצר:Binja</span>
                    חיפוש סרטונים רק של יוצר התוכן, במקרה הזה בינג&apos;ה.
                </p>
            }
            <p><span className='bg-black/50 rounded px-1 me-2'>+מילה</span>
                חייב להכיל את המילה.
            </p>
            <p><span className='bg-black/50 rounded px-1 me-2'>-מילה</span>
                אסור להכיל את המילה.
            </p>
        </PopoverContent>
    </Popover>
}